import React, { useCallback, useEffect, useState } from 'react';
import { REACT_APP_SSO_KEY } from '../localStorageKeys';
import Loader from '../components/Loader';

export class ErrorObject extends Error {
  errors: unknown;

  code: number;

  constructor(message, code, errors) {
    super(message);
    this.name = 'Error';
    this.code = code;
    this.errors = errors;
  }
}

export const getError = (code: number, errors: unknown = {}) => {
  const errorObject = new ErrorObject('Error in API call', code, errors);

  return errorObject;
};

export const apiFetch = async (
  url: string,
  body = {},
  method = 'GET',
  userHeaders: HeadersInit = {},
  credentials: RequestCredentials = undefined,
  formDataBody: FormData = undefined,
  plain = false
) => {
  const options: RequestInit = {};

  options.headers = {
    ...(!formDataBody && {
      'Content-Type': 'application/json',
    }),
    ...userHeaders,
  };

  const METHODS_WITHOUT_BODY = ['GET', 'DELETE'];
  if (!METHODS_WITHOUT_BODY.includes(method)) {
    options.body = formDataBody || JSON.stringify(body);
  }

  options.method = method;
  options.credentials = credentials;

  return fetch(url, {
    ...options,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }).then(async (response): Promise<any> => {
    if (response.ok && plain) {
      return response.text();
    }
    if (response.ok) {
      return response
        .json()
        .catch(() => response)
        .then((responseData) =>
          responseData && responseData.data && Object.keys(responseData).length === 1
            ? responseData.data
            : responseData
        );
    }

    return response
      .json()
      .catch(() => {
        throw getError(response.status, response.statusText);
      })
      .then((error) => {
        throw getError(Number(error.code || response.status), error.errors || error);
      });
  });
};

const apiFetchDefault = (
  url: string,
  body: unknown = {},
  method = 'GET',
  extraHeaders = {},
  formDataBody: FormData = undefined,
  plain = false
) => apiFetch(url, body, method, extraHeaders, undefined, formDataBody, plain);

const ApiContext = React.createContext({
  fetchIsReady: false,
  apiFetch: apiFetchDefault,
  verificationCode: '',
  username: '',
});

const ApiContextProvider = ({ children }: { children: JSX.Element }) => {
  const [fetchIsReady, setFetchIsReady] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState(undefined);
  const [username, setUsername] = useState(undefined);
  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    if (!params) window.location.href = `${localStorage.getItem(REACT_APP_SSO_KEY)}`;

    const paramsCode = params.get('code') || sessionStorage.getItem('code');
    const paramsUsername = params.get('username') || sessionStorage.getItem('username');

    if (!paramsCode || !paramsUsername)
      window.location.href = `${localStorage.getItem(REACT_APP_SSO_KEY)}`;

    if (paramsCode && (!verificationCode || paramsCode !== verificationCode)) {
      sessionStorage.setItem('code', paramsCode);
      setVerificationCode(paramsCode);
    }
    if (paramsUsername && (!username || paramsUsername !== username)) {
      sessionStorage.setItem('username', paramsUsername);
      setUsername(paramsUsername);
    }
  }, [params]);

  useEffect(() => {
    if (!fetchIsReady && verificationCode && username) setFetchIsReady(true);
  }, [verificationCode, username]);

  const apiFetchUsingUserParams = useCallback(
    async (
      url: string,
      body: unknown = {},
      method = 'GET',
      extraHeaders: HeadersInit = {},
      formDataBody: FormData = undefined,
      plain = false
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ): Promise<any> => {
      if (!fetchIsReady) {
        throw getError(
          401,
          'Fetch is not ready, please be sure to enable queries using supplied fetchIsReady variable.'
        );
      }

      return apiFetch(
        `${url}${
          url?.includes('?') ? '&' : '?'
        }authUsername=${username}&authActivationCode=${verificationCode}`,
        body,
        method,
        extraHeaders,
        undefined,
        formDataBody,
        plain
      );
    },
    [fetchIsReady]
  );

  if (!fetchIsReady) {
    return <Loader ariaBusy={!fetchIsReady} />;
  }

  return (
    <ApiContext.Provider
      value={{
        fetchIsReady,
        apiFetch: apiFetchUsingUserParams,
        verificationCode,
        username,
      }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export { ApiContext, ApiContextProvider };
